/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from "react";
import Collapse from "react-bootstrap/Collapse";
import TableBtnDetail from "components/fragments/TableBtnDetail";
import ProductOrderDetail from "pages/pos-menus/pos/ProductOrderDetail";
import ReceiptPrintPopup from "components/modals/printing-forms/ModalReceiptPrint";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { billHandler, memberHandler } from "../../redux/handlers";
import {
  setOrderId,
  setShouldOpenPaymentModal,
} from "../../redux/slices/pos.slice";
import { useParams } from "react-router-dom";

const handleImageError = (e) => {
  e.target.onerror = null;
  e.target.src = "/images/img-default.jpeg";
};

export default function ShowTableDetail({
  isDetailModeObj,
  currentTableOrder,
  currentZoneName,
}) {
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { businessSlug } = useParams();
  const [orderData, setOrderData] = useState([]);
  const [isShownOrder, setIsShownOrder] = useState(false);
  const [billData, setBillData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTotalExpanded, setIsTotalExpanded] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const notificationPing = useSelector((state) => state.kitchen.notificationSocket);

  const modalRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isDetailModeObj.isDetailMode &&
        modalRef.current &&
        !modalRef.current.contains(event.target)
      ) {
        isDetailModeObj.setIsDetailMode(false);
      }
    };

    if (isDetailModeObj.isDetailMode) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isDetailModeObj.isDetailMode, isDetailModeObj.setIsDetailMode]);

  useEffect(() => {
    if (currentTableOrder !== undefined && currentTableOrder !== null) {
      if (isDetailModeObj.isDetailMode) {
        fetchTableBillData();
      } else {
        setOrderData([]);
        setBillData(null);
      }
    }
  }, [dispatch, currentTableOrder, isDetailModeObj.isDetailMode]);

  // [fix] auto-refresh order list when pos_notification fires with 'order' type (new items added)
  useEffect(() => {
    if (!notificationPing || !isDetailModeObj.isDetailMode || !currentTableOrder) return;
    const msg = typeof notificationPing === 'object' ? notificationPing : {};
    if (msg.raw === 'order') {
      fetchTableBillData();
    }
  }, [notificationPing]);

  // [fix] Periodic refresh fallback every 10s when modal is open — catches missed pos_notification events
  // (socket death after reconnectionAttempts exhausted means notifications may not arrive)
  useEffect(() => {
    if (!isDetailModeObj.isDetailMode || !currentTableOrder) return;
    const interval = setInterval(() => {
      fetchTableBillData();
    }, 10000);
    return () => clearInterval(interval);
  }, [isDetailModeObj.isDetailMode, currentTableOrder]);

  useEffect(() => {
    if (
      currentTableOrder?.type === "reserved" ||
      currentTableOrder?.type === "empty"
    ) {
      setIsShownOrder(false);
    } else {
      setIsShownOrder(true);
    }
  }, [currentTableOrder]);

  const setBlankData = () => {
    setLoading(false);
    setBillData({
      order: {
        code: "",
      },
      created_at: new Date().toISOString(),
      total_price: "0.00",
      taxable_amount: "0.00",
      vat: "0.00",
      member: {
        name: "",
        phone: "",
        tier: "-",
      },
      reservation: {
        customer_name: "",
        customer_phone: "",
      },
    });

    setOrderData([]);
  };

  const fetchTableBillData = async () => {
    if (!currentTableOrder?.tableID) return;

    setLoading(true);
    try {
      const response = await dispatch(
        billHandler.getBillByBranchTableId(
          businessSlug,
          currentTableOrder.tableID
        )
      );

      if (response && response.length > 0) {
        const billInfo = response[0];
        setBillData(billInfo);

        const formatItem = (orderItem) => {
          const product = orderItem.product || {};
          const annotationOptions =
            orderItem.orderProductAnnotation?.map((note) => ({
              itemName: note.productAnnotationItem?.name || "",
              listID: note.id,
            })) || [];
          const specificOptions = [
            ...(orderItem.orderProductOption?.map((option) => ({
              itemName: option.productOptionItem?.name || "",
              listID: option.id,
            })) || []),
            ...(orderItem.orderProductSpecificOption?.map((option) => ({
              itemName: option.productSpecificOptionItem?.name || "",
              listID: option.id,
            })) || []),
          ];
          const standardOptions = [];
          let productStatus = 1;
          const isCancel = orderItem.status_kitchen === "ORDER_CANCEL";
          switch (orderItem.status_kitchen) {
            case "PENDING": productStatus = 1; break;
            case "SUCCESS": productStatus = 2; break;
            case "CANCEL": productStatus = 3; break;
            case "ORDER_CANCEL": productStatus = 4; break;
            default: productStatus = 1;
          }
          const effectiveQuantity = (orderItem.quantity || 0) - (orderItem.quantity_removed || 0);
          return {
            id: orderItem.id,
            productID: product.id,
            productImage: product.image_path || "",
            productName: product.name || orderItem.item_name || "",
            productPrice: (orderItem.price || 0).toString(),
            productQty: effectiveQuantity,
            productSumPrice: parseFloat(orderItem.price || 0),
            displayPrice: orderItem.price || 0,
            productStatus,
            isCancel,
            annotationOptions,
            specificOptions,
            standardOptions,
            note: orderItem.remark || "",
          };
        };

        // collect items from ALL bills — table may have multiple orders (DINE_IN + TAKEAWAY etc.)
        // strategy: prefer order.orderItems (real-time, includes เพิ่มรายการ) then union billItems
        // dedup by orderItem.id so items from both sources are never double-counted
        const allItems = [];
        const seenIds = new Set();
        for (const bill of response) {
          const liveItems = bill.order?.orderItems;
          if (Array.isArray(liveItems) && liveItems.length > 0) {
            for (const oi of liveItems) {
              if (!seenIds.has(oi.id)) {
                seenIds.add(oi.id);
                allItems.push(formatItem(oi));
              }
            }
          }
          // also include billItems — catches items when orderItems is empty (POS-created bill)
          if (bill.billItems?.length > 0) {
            for (const bi of bill.billItems) {
              if (bi.orderItem && !seenIds.has(bi.orderItem.id)) {
                seenIds.add(bi.orderItem.id);
                allItems.push(formatItem(bi.orderItem));
              }
            }
          }
        }
        setOrderData(allItems);

        if (billInfo?.member_id) {
          const memberRes = await dispatch(
            memberHandler.getMemberById(businessSlug, billInfo.member_id)
          );

          const memberPayload = memberRes;
          if (memberPayload) {
            setBillData((prev) => ({
              ...prev,
              member: {
                ...(prev?.member ?? {}),
                ...memberPayload,
                total_point:
                  memberPayload.total_point ?? prev?.member?.total_point,
              },
            }));
          }
        }
      } else {
        setBlankData();
      }
    } catch (error) {
      console.error("Error fetching table bill data:", error);
      setBlankData();
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    // [fix] use live orderItems sum — productSumPrice = orderItem.price (TOTAL per line, not unit)
    //       billData.total_price is a snapshot and excludes items added after bill creation
    if (orderData && orderData.length > 0) {
      return orderData
        .filter((item) => !item.isCancel && item.productStatus !== 4)
        .reduce((sum, item) => sum + parseFloat(item.productSumPrice || 0), 0)
        .toFixed(2);
    }
    if (billData && billData.total_price) {
      return parseFloat(billData.total_price).toFixed(2);
    }
    return "0.00";
  };

  const formatAmount = (num) => {
    if (!num) return "0.00";
    const numStr = num.toString();
    const [intPart, decimalPart = ""] = numStr.split(".");
    let formattedDecimal;
    if (decimalPart.length === 0) {
      formattedDecimal = "00";
    } else if (decimalPart.length === 1) {
      formattedDecimal = decimalPart + "0";
    } else {
      formattedDecimal = decimalPart.substring(0, 2);
    }
    return `${intPart}.${formattedDecimal}`;
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    const date = new Date(isoString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const calculateEndTime = (openedAt, duration) => {
    if (!openedAt) return "-";
    const openTime = new Date(openedAt);
    if (!duration) return "-";

    openTime.setMinutes(openTime.getMinutes() + duration);
    return openTime.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const isTableOvertime = (openedAt, timeDurationMinutes) => {
    try {
      if (!openedAt || !timeDurationMinutes) return false;

      const openedDate = new Date(openedAt);
      const endDate = new Date(
        openedDate.getTime() + timeDurationMinutes * 60 * 1000
      );
      const currentTime = new Date();

      return currentTime > endDate;
    } catch (error) {
      console.error("Error checking overtime:", error);
      return false;
    }
  };

  const handleProductClick = (product) => {
    return false;
  };

  const handlePrintBill = () => {
    setPrintModalOpen(true);
  };

  const handlePrintComplete = () => {
    setPrintModalOpen(false);
  };

  const handleAddMoreItems = () => {
    if (billData?.order?.id) {
      dispatch(setOrderId(billData.order.id));
      navigate(`/${businessSlug}`);
    }
  };

  const handleCheckBill = () => {
    if (billData?.order?.id) {
      dispatch(setOrderId(billData.order.id));
      dispatch(setShouldOpenPaymentModal(true));
      navigate(`/${businessSlug}`);
    }
  };


  return (
    <div
      className={`bg-[#fff] w-[min(48.93vw,501px)] h-full fixed top-0 right-0 pt-[min(1.66vw,17px)] table-detail ${
        isDetailModeObj.isDetailMode ? "active" : ""
      }`}
      ref={modalRef}
    >
      <div className="relative h-full">
        <div className="px-[min(1.95vw,20px)]">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-[min(1.66vw,17px)]">
              <img
                onClick={() => isDetailModeObj.setIsDetailMode(false)}
                className="cursor-pointer object-contain aspect-[15.07/15.75] w-[min(1.47vw,15.07px)]"
                src="/img/icons/icon-double-arrow-right-red.svg"
                alt=""
                onError={handleImageError}
              />
              <div className="text-18 leading-[1.3] flex flex-col gap-[min(0.29vw,3px)]">
                <div className="text-[var(--color-grey-2)] font-[500]">
                  Order NO.{" "}
                  <span className="text-[var(--color-green)]">
                    {billData?.order?.code || "-"}
                  </span>
                </div>
                <div className="flex gap-[min(0.29vw,3px)]">
                  <img
                    className="object-contain aspect-square w-[min(1.76vw,18px)]"
                    src="/img/icons/icon-calendar-green.svg"
                    alt=""
                    onError={handleImageError}
                  />
                  <div className="font-[300] text-[var(--color-grey-2)]">
                    {billData?.created_at
                      ? new Date(billData.created_at).toLocaleDateString(
                          "th-TH",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          }
                        )
                      : "-"}
                  </div>
                </div>
              </div>
            </div>

            <img
              onClick={() => isDetailModeObj.setIsDetailMode(false)}
              className="cursor-pointer object-contain aspect-[15.07/15.75] w-[min(1.47vw,15.07px)] h-fit"
              src="/img/icons/icon-double-arrow-right-red.svg"
              alt=""
              onError={handleImageError}
            />
          </div>

          <div className="mt-[min(1.07vw,11px)] flex w-full rounded-[min(0.78vw,8px)] bg-[#F4FCF8] px-[min(1.56vw,16px)] pt-[min(0.59vw,6px)] pb-[min(0.78vw,8px)] ">
            <div className="flex flex-col items-center justify-between w-fit mr-[min(3.47vw,35.5px)]">
              <img
                className="object-cover rounded-full aspect-square w-[min(3.42vw,35px)] h-fit"
                src="/img/avartar-male.png"
                alt=""
                onError={handleImageError}
              />
              <div className="flex text-10 text-[var(--color-grey-2)] items-center gap-[min(0.39vw,4px)]">
                <img
                  className="object-contain aspect-[11.51/7.97] w-[min(1.12vw,11.51px)] h-fit"
                  src="/img/star-yellow.svg"
                  alt=""
                  onError={handleImageError}
                />
                <div>{billData?.member?.total_point ?? "-"}</div>
              </div>
            </div>
            <div className="flex-grow">
              <div className="truncate text-[#4C4E64DE] font-[500] tracking-[0.015em]">
                {billData?.member?.first_name_th
                  ? `${billData.member.first_name_th} ${
                      billData.member.last_name_th || ""
                    }`.trim()
                  : ""}
              </div>
              <div className="truncate text-[var(--color-grey-2)] tracking-[0.04em]">
                {billData?.member?.phone_number || ""}
              </div>
            </div>
            <div className="flex flex-col items-end justify-between">
              <div className="cursor-pointer flex items-center gap-[min(0.59vw,6px)]">
                <div className="text-12 text-[var(--color-green)] underline">
                  แก้ไข
                </div>
                <img
                  className="object-contain aspect-square w-[min(1.06vw,10.9px)] h-fit"
                  src="/img/icons/icon-edit-green.svg"
                  alt=""
                  onError={handleImageError}
                />
              </div>
              <div className="rounded-full min-w-[min(8.13vw,83.3px)] h-[min(1.65vw,16.85px)] bg-[var(--color-blue-2)] flex justify-center items-center text-[#FFF] text-12 font-[300] mb-[min(0.29vw,3px)]">
                -
              </div>
            </div>
          </div>

          <div className="flex gap-[min(2.44vw,25px)] text-16 mt-[min(1.66vw,17px)]">
            <div className="w-[40%]">
              <div className="flex items-center gap-[min(0.88vw,9px)]">
                <div className="truncate text-[var(--color-green)] font-[700]">
                  จำนวนลูกค้า :
                </div>
                <img
                  className="object-contain rounded-full aspect-[13/15] w-[min(1.27vw,13px)] h-fit"
                  src="/img/icons/icon-table-user-green.svg"
                  alt=""
                  onError={handleImageError}
                />
                <div>
                  <span className="font-[700] text-[var(--color-green)]">
                    {currentTableOrder?.seat || 0}
                  </span>
                  <span>/</span>
                  <span>{currentTableOrder?.seat_capacity || 0}</span>
                </div>
              </div>
              <div className="flex items-center gap-[min(0.88vw,9px)]">
                <div className="truncate text-[var(--color-green)] font-[700]">
                  เวลาเปิดโต๊ะ :
                </div>
                <div className="text-[var(--color-green)]">
                  {formatTime(currentTableOrder?.opened_at)}
                </div>
              </div>
            </div>
            <div className="flex-grow items-center text-[var(--color-red)]">
              <div className="flex items-baseline gap-[min(0.88vw,9px)]">
                <div className="whitespace-nowrap font-[700]">โต๊ะ :</div>
                <div className="whitespace-nowrap">
                  {currentZoneName} : {currentTableOrder?.tableNo || "-"}
                </div>
                {currentTableOrder?.moveFrom && (
                  <div className="text-12 text-[#949494]">
                    (ย้ายจาก {currentTableOrder?.moveFrom} )
                  </div>
                )}
                {currentTableOrder?.mergeList && (
                  <div className="text-12 text-[#949494]">
                    (รวม {currentTableOrder?.mergeList} )
                  </div>
                )}

                {currentTableOrder?.merged_tables &&
                  currentTableOrder.merged_tables.length > 0 && (
                    <div className="text-12 text-[#949494]">
                      (รวม{" "}
                      {currentTableOrder.merged_tables
                        .map((table) => table.table_name)
                        .join(", ")}{" "}
                      )
                    </div>
                  )}
              </div>
              <div className="flex items-center gap-[min(0.88vw,9px)]">
                <div className="font-[700]">เวลากำหนด :</div>
                <div>
                  {calculateEndTime(
                    currentTableOrder?.opened_at,
                    currentTableOrder?.time_duration
                  )}
                  {isTableOvertime(
                    currentTableOrder?.opened_at,
                    currentTableOrder?.time_duration
                  ) && (
                    <span className="text-sm text-[var(--color-red)] font-[500] ml-[min(0.39vw,4px)]">
                      ( เกินกำหนด)
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {currentTableOrder?.type === "reserved" && (
          <>
            <div className="w-full h-[min(0.10vw,1px)] bg-[rgba(182,187,198,0.3)] mt-[min(2.83vw,29px)]"></div>
            <div className="px-[min(1.95vw,20px)] text-16 text-[var(--color-grey-2)] mt-[min(3.52vw,36px)]">
              <div className="flex justify-between items-center">
                <div className="font-[700]">ชื่อผู้จอง :</div>
                <div>{currentTableOrder?.customerName || "-"}</div>
              </div>
              <div className="flex justify-between items-center">
                <div className="font-[700]">เบอร์โทรศัพท์ :</div>
                <div>{billData?.reservation?.customer_phone || "-"}</div>
              </div>
            </div>
          </>
        )}

        {isShownOrder && (
          <>
            <div className="row g-0 mt-[min(1.76vw,18px)] px-[min(1.95vw,20px)]">
              <div className="row gx-2 tc-17 fs-12">
                <div className="col-7">รายการ</div>
                <div className="col-2 text-center">จำนวน</div>
                <div className="col-3 text-end">ราคา</div>
              </div>
            </div>
            <div className="w-full h-[min(0.10vw,1px)] bg-[rgba(182,187,198,0.3)] mt-[min(0.98vw,10px)]"></div>

            {loading ? (
              <div className="flex justify-center items-center py-10">
                <div className="text-[var(--color-grey-2)]">
                  กำลังโหลดข้อมูล...
                </div>
              </div>
            ) : orderData && orderData.length > 0 ? (
              <div
                className={`bx-main-order-list px-[min(1.95vw,20px)] ${
                  windowHeight > 767 ? "max-h-[45vh]" : "max-h-[35vh]"
                } overflow-y-auto`}
                style={{ height: "calc(100vh - 480px)" }}
              >
                <ul className="p-0 m-0 w-100">
                  <ProductOrderDetail
                    products={orderData}
                    handleClick={handleProductClick}
                  />
                </ul>
              </div>
            ) : (
              <div className="flex justify-center items-center py-10">
                <div className="text-[var(--color-grey-2)]">
                  ไม่พบรายการอาหาร
                </div>
              </div>
            )}
          </>
        )}

        <div className="absolute bottom-0 w-full h-fit">
          <div className="px-[min(1.95vw,20px)] pb-2">
            <Collapse in={isTotalExpanded}>
              <div id="collapse1">
                <div className="card card-body border-0 bg-[#F4FCF8] px-3 py-2 mb-2 rounded-[min(0.78vw,8px)]">
                  <div className="d-flex fs-14 tc-10 px-1">
                    <div className="me-auto">ยอดรวม</div>
                    <div className="" id="txsum">
                      {calculateTotal()}฿
                    </div>
                  </div>
                  <div className="d-flex fs-12 tc-10 px-1">
                    <div className="me-auto">มูลค่าสินค้าไม่รวมภาษี</div>
                    <div id="txdiscount">
                      {formatAmount(billData?.taxable_amount || 0)}฿
                    </div>
                  </div>
                  <div className="d-flex fs-12 tc-10 px-1">
                    <div className="me-auto">ภาษี</div>
                    <div id="txtax">{formatAmount(billData?.vat || 0)}฿</div>
                  </div>
                </div>
              </div>
            </Collapse>

            <button
              className="btn collapse-total collapsed mb-3 w-full"
              type="button"
              onClick={() => setIsTotalExpanded(!isTotalExpanded)}
              aria-controls="collapse1"
              aria-expanded={isTotalExpanded}
              style={{
                background: "#CAE0FD",
                color: "var(--color-blue-2)",
                border: "none",
                borderRadius: "min(0.78vw,8px)",
                padding: "min(1.17vw,12px) min(1.95vw,20px)",
                fontSize: "18px",
                fontWeight: "500",
              }}
            >
              <div className="d-flex align-items-center">
                <img
                  className="object-contain aspect-[8.79/14.98] w-[min(0.86vw,8.79px)] mr-[min(1.95vw,20px)]"
                  src="/img/icons/icon-arrow-up-blue.svg"
                  alt=""
                  onError={handleImageError}
                />
                <div className="me-auto">ยอดรวมทั้งหมด</div>
                <div id="txnet">{calculateTotal()}฿</div>
              </div>
            </button>
          </div>

          <div className="flex justify-center bg-[#F3F3F3] py-[min(1.17vw,12px)] gap-[min(1.17vw,12px)]">
            <TableBtnDetail
              className="flex justify-center text-[#FFF] items-center btn-table-zone-active shadow-table-btn-2 text-14 min-w-[min(13.38vw,137px)] h-[min(3.32vw,34px)] font-[500]"
              content={
                <div className="flex gap-[min(0.59vw,6px)]">
                  <img
                    className="object-contain aspect-[22.5/20.25] w-[min(2.20vw,22.5px)]"
                    src={
                      currentTableOrder?.type !== "reserved" &&
                      orderData?.length > 0
                        ? "/img/icons/icon-print-white.svg"
                        : "/img/icons/icon-print-grey.svg"
                    }
                    alt=""
                    onError={handleImageError}
                  />
                  <div>พิมพ์รายการ</div>
                </div>
              }
              isActive={
                currentTableOrder?.type !== "reserved" && orderData?.length > 0
              }
              onClickBtn={
                currentTableOrder?.type !== "reserved" && orderData?.length > 0
                  ? handlePrintBill
                  : undefined
              }
            />
            <TableBtnDetail
              className="flex justify-center text-[#FFF] items-center btn-orange shadow-table-btn-2 text-14 min-w-[min(10.64vw,109px)] h-[min(3.32vw,34px)] font-[500]"
              content={<div>เพิ่มรายการ</div>}
              isActive={
                currentTableOrder?.type !== "reserved" && orderData?.length > 0
              }
              onClickBtn={
                currentTableOrder?.type !== "reserved" && orderData?.length > 0
                  ? handleAddMoreItems
                  : undefined
              }
            />
            <TableBtnDetail
              className="flex justify-center text-[#FFF] items-center btn-main-active shadow-table-btn-2 text-14 min-w-[min(10.64vw,109px)] h-[min(3.32vw,34px)] font-[500]"
              content={<div>เช็คบิล</div>}
              isActive={
                currentTableOrder?.type !== "reserved" && orderData?.length > 0
              }
              onClickBtn={
                currentTableOrder?.type !== "reserved" && orderData?.length > 0
                  ? handleCheckBill
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <ReceiptPrintPopup
        isOpen={printModalOpen}
        onPrintComplete={handlePrintComplete}
        paymentResponse={billData}
      />
    </div>
  );
}
